import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import Button from '../Button';

const TOKENS = ['firstName', 'lastName', 'school', 'pos', 'classYear', 'coachName'];

const TOKEN_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  school: 'School',
  pos: 'Position',
  classYear: 'Class year',
  coachName: 'Coach name',
};

function getAxiosConfig() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

/** Render a parts array to a plain preview string (tokens shown as {{token}}). */
function previewString(parts) {
  return (parts || [])
    .map((p) => (p.kind === 'token' ? `{{${p.value}}}` : p.value))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * TemplateEditor — create or edit a message template.
 *
 * Props:
 *  - template:  existing template to edit (or null/undefined for a blank new one)
 *  - onSaved:   () => void  called after a successful save
 *  - onCancel:  () => void
 */
export default function TemplateEditor({ template, onSaved, onCancel }) {
  const { API_URL } = useAuth();

  const isFork = !!(template && template.is_system);
  const isEditExisting = !!(template && !template.is_system && template.id);

  const [name, setName] = useState(
    template ? (isFork ? `${template.name} (copy)` : template.name) : ''
  );
  const [parts, setParts] = useState(template ? [...(template.parts || [])] : []);
  const [draftText, setDraftText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Append the draft text to a parts array as a (merged) text part. Pure — no
  // side effects, so it's safe to call inside a setParts updater (StrictMode
  // runs updaters twice). Caller is responsible for clearing draftText.
  const appendDraftText = (current) => {
    const text = draftText;
    if (!text) return current;
    const next = [...current];
    const last = next[next.length - 1];
    if (last && last.kind === 'text') {
      next[next.length - 1] = { kind: 'text', value: last.value + text };
    } else {
      next.push({ kind: 'text', value: text });
    }
    return next;
  };

  const handleAddToken = (token) => {
    setParts((prev) => [...appendDraftText(prev), { kind: 'token', value: token }]);
    setDraftText('');
  };

  const commitDraftText = () => {
    setParts((prev) => appendDraftText(prev));
    setDraftText('');
  };

  const handleRemovePart = (idx) => {
    setParts((prev) => prev.filter((_, i) => i !== idx));
  };

  // Build the effective parts array (committing the draft text) for save/preview.
  const effectiveParts = () => {
    if (!draftText) return parts;
    const next = [...parts];
    const last = next[next.length - 1];
    if (last && last.kind === 'text') {
      next[next.length - 1] = { kind: 'text', value: last.value + draftText };
    } else {
      next.push({ kind: 'text', value: draftText });
    }
    return next;
  };

  const handleSave = async () => {
    setError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a name.');
      return;
    }
    const finalParts = effectiveParts();
    if (!finalParts.length) {
      setError('Add some text or a token to the template body.');
      return;
    }

    setSaving(true);
    try {
      if (isEditExisting) {
        await axios.put(
          `${API_URL}/coach-templates/${template.id}`,
          { name: trimmedName, parts: finalParts },
          getAxiosConfig()
        );
      } else {
        // New template OR fork of a system template — always POST a new custom copy.
        await axios.post(
          `${API_URL}/coach-templates/`,
          { name: trimmedName, parts: finalParts },
          getAxiosConfig()
        );
      }
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template.');
      setSaving(false);
    }
  };

  const displayParts = effectiveParts();

  return (
    <div className="template-editor">
      <div className="modal-title" style={{ marginBottom: '16px' }}>
        {isFork ? 'Save a copy' : isEditExisting ? 'Edit template' : 'New template'}
      </div>

      {isFork && (
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
          System templates can't be changed. This will save your own editable copy.
        </p>
      )}

      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
        Name
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Intro outreach"
        aria-label="Template name"
        style={{
          width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0',
          fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box',
        }}
      />

      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>
        Insert a field
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {TOKENS.map((t) => (
          <Button
            key={t}
            type="button"
            variant="secondary"
            className="template-token-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleAddToken(t)}
            style={{
              padding: '4px 10px', borderRadius: '14px', border: '1px solid #c7d2fe',
              background: '#eef2ff', color: '#4338ca', fontSize: '12px',
            }}
          >
            + {TOKEN_LABELS[t]}
          </Button>
        ))}
      </div>

      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>
        Body
      </label>
      <div
        className="template-parts-builder"
        style={{
          border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px',
          minHeight: '70px', marginBottom: '14px', display: 'flex',
          flexWrap: 'wrap', gap: '4px', alignItems: 'center', lineHeight: 1.8,
        }}
      >
        {parts.map((p, idx) =>
          p.kind === 'token' ? (
            <span
              key={idx}
              className="template-chip"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '2px 8px', borderRadius: '12px', background: '#4338ca',
                color: '#fff', fontSize: '12px',
              }}
            >
              {TOKEN_LABELS[p.value] || p.value}
              <Button
                type="button"
                variant="ghost"
                iconOnly
                onClick={() => handleRemovePart(idx)}
                aria-label={`Remove ${p.value}`}
                style={{
                  background: 'none', border: 'none', color: '#fff',
                  fontSize: '13px', lineHeight: 1, padding: 0,
                }}
              >
                &times;
              </Button>
            </span>
          ) : (
            <span key={idx} style={{ whiteSpace: 'pre-wrap' }}>{p.value}</span>
          )
        )}
        <input
          type="text"
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          onBlur={() => commitDraftText()}
          placeholder={parts.length ? 'Continue typing…' : 'Type your message…'}
          aria-label="Template body text"
          style={{
            flex: '1 1 120px', minWidth: '120px', border: 'none', outline: 'none',
            fontSize: '14px', padding: '2px',
          }}
        />
      </div>

      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
        Preview
      </label>
      <div
        style={{
          background: '#f8fafc', borderRadius: '8px', padding: '10px', fontSize: '13px',
          color: '#334155', marginBottom: '16px', minHeight: '36px', lineHeight: 1.5,
        }}
      >
        {previewString(displayParts) || <span style={{ color: '#94a3b8' }}>Nothing yet</span>}
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export { previewString };
